import { ReactNode } from 'react';
import { DateRange } from 'react-day-picker';

import { StringKey } from '../../../../../lang/index';
import {
  EventExercisedTypeBackend,
  EventTypeBackend,
  EventUnion,
  SortByDate,
} from '../../../../../types/events.types';

export const itemsToFetchMap = [5, 10, 20, 50, 100];

export const EVENT_TYPES_WITH_EVENT_LEVEL_SIGNATURE_ONLY: EventTypeBackend[] = [
  EventTypeBackend.CONVERSION,
  EventTypeBackend.NEW_PLAN,
  EventTypeBackend.NEW_POOL,
  EventTypeBackend.VALUATION,
];

export type TableBodyProps = {
  companyId: string;
  inCurrentRound: boolean;
  name: ReactNode | string;
  onDelete: (event: EventUnion, transactionId: string) => void;
  onEdit: (id: string, transactionId: string) => void;
  onView: (id: string, transactionId: string) => void;
  selectedCustomDateFilter: DateRange;
  selectedDateFilter: SortByDate;
  transactionView?: boolean;
} & EventUnion;

// A pool increase and a pool decrease show the same columns.
const poolAdjustmentTableHead: StringKey[] = [
  'TRANSACTION_DATE',
  'TRANSACTION_TYPE',
  'NAME',
  'NEW_NO_OF_FULLY_DILUTED_SHARES',
  'SHARE_CLASS',
  'EMPTY',
];

export const tableHeadVariant: Record<EventExercisedTypeBackend | EventTypeBackend, StringKey[]> = {
  [EventExercisedTypeBackend.STOCK_OPTIONS]: [
    'TRANSACTION_DATE',
    'TRANSACTION_TYPE',
    'STAKEHOLDER',
    'GRANT_ID',
    'EXERCISED_OPTIONS',
    'SHARE_CLASS',
    'SIGNATURE_STATUS',
    'FILE',
  ],

  [EventExercisedTypeBackend.STOCK_OPTIONS_TERMINATED]: [
    'TRANSACTION_DATE',
    'TRANSACTION_TYPE',
    'STAKEHOLDER',
    'GRANT_ID',
    'EXERCISED_OPTIONS',
    'SHARE_CLASS',
    'RETURNED_TO_POOL',
    'SIGNATURE_STATUS',
    'FILE',
  ],

  [EventExercisedTypeBackend.SAR]: [
    'TRANSACTION_DATE',
    'TRANSACTION_TYPE',
    'STAKEHOLDER',
    'GRANT_ID',
    'SAR_EXERCISED',
    'SHARE_CLASS',
    'SAR_PAYOUT',
    'SIGNATURE_STATUS',
    'FILE',
  ],
  [EventExercisedTypeBackend.SAR_TERMINATED]: [
    'TRANSACTION_DATE',
    'TRANSACTION_TYPE',
    'STAKEHOLDER',
    'GRANT_ID',
    'SAR_EXERCISED',
    'SHARE_CLASS',
    'SAR_PAYOUT',
    'RETURNED_TO_POOL',
    'SIGNATURE_STATUS',
    'FILE',
  ],

  [EventExercisedTypeBackend.RSA]: [
    'TRANSACTION_DATE',
    'TRANSACTION_TYPE',
    'STAKEHOLDER',
    'EXERCISED_OPTIONS',
    'SHARE_CLASS',
    'PURCHASE_PRICE',
    'SIGNATURE_STATUS',
    'FILE',
  ],

  [EventExercisedTypeBackend.RSA_TERMINATED]: [
    'TRANSACTION_DATE',
    'TRANSACTION_TYPE',
    'STAKEHOLDER',
    'EXERCISED_OPTIONS',
    'SHARE_CLASS',
    'PURCHASE_PRICE',
    'RETURNED_TO_POOL',
    'SIGNATURE_STATUS',
    'FILE',
  ],

  [EventExercisedTypeBackend.WARRANTS]: [
    'TRANSACTION_DATE',
    'TRANSACTION_TYPE',
    'STAKEHOLDER',
    'GRANT_ID',
    'EXERCISED_WARRANTS',
    'SHARE_CLASS',
    'SIGNATURE_STATUS',
    'FILE',
  ],

  [EventExercisedTypeBackend.WARRANTS_TERMINATED]: [
    'TRANSACTION_DATE',
    'TRANSACTION_TYPE',
    'STAKEHOLDER',
    'GRANT_ID',
    'EXERCISED_WARRANTS',
    'SHARE_CLASS',
    'RETURNED_TO_POOL',
    'SIGNATURE_STATUS',
    'FILE',
  ],

  [EventExercisedTypeBackend.PHANTOM]: [
    'STAKEHOLDER',
    'GRANT',
    'PAYOUT_DATE',
    'SHARE_PRICE',
    'PHANTOM_SHARES',
    'TOTAL_PAYOUT',
    'SIGNATURE_STATUS',
    'FILE',
  ],

  [EventExercisedTypeBackend.PHANTOM_TERMINATED]: [
    'STAKEHOLDER',
    'GRANT',
    'PAYOUT_DATE',
    'SHARE_PRICE',
    'PHANTOM_SHARES',
    'TOTAL_PAYOUT',
    'RETURNED_TO_POOL',
    'SIGNATURE_STATUS',
    'FILE',
  ],

  [EventTypeBackend.EXERCISE]: [
    'TRANSACTION_DATE',
    'TRANSACTION_TYPE',
    'STAKEHOLDER',
    'GRANT_ID',
    'EXERCISED_GRANTS',
    'SHARE_CLASS',
    'SIGNATURE_STATUS',
    'FILE',
  ],

  [EventTypeBackend.APPROVAL]: [
    'TRANSACTION_DATE',
    'TRANSACTION_TYPE',
    'STAKEHOLDER',
    'GRANT_ID',
    'EXERCISED_GRANTS',
    'SHARE_CLASS',
    'SIGNATURE_STATUS',
    'FILE',
  ],

  [EventTypeBackend.PAYOUT]: [
    'TRANSACTION_DATE',
    'TRANSACTION_TYPE',
    'STAKEHOLDER',
    'SHARES_PAID_OUT',
    'TOTAL_PAYOUT',
    'RETURNED_TO_POOL',
    'SIGNATURE_STATUS',
    'FILE',
  ],

  [EventTypeBackend.EXPIRE]: [
    'TRANSACTION_DATE',
    'TRANSACTION_TYPE',
    'STAKEHOLDER',
    'GRANT_ID',
    'EXPIRED_WARRANTS',
    'SHARE_CLASS',
    'EXPIRY_DATE',
    'SIGNATURE_STATUS',
    'FILE',
  ],

  [EventTypeBackend.CONVERSION]: [
    'TRANSACTION_DATE',
    'STAKEHOLDER',
    'FROM_SHARE_CLASS',
    'TO_SHARE_CLASS',
    'NO_OF_SHARES',
    'CONVERTED_SHARES',
    'SIGNATURE_STATUS',
    'FILE',
    'EMPTY',
  ],
  [EventTypeBackend.GRANT]: [
    'TRANSACTION_DATE',
    'STAKEHOLDER',
    'GRANT_ID',
    'NO_OF_SHARES',
    'EQUITY_PLAN',
    'SIGNATURE_STATUS',
    'FILE',
    'EMPTY',
  ],
  [EventTypeBackend.BUYBACK]: [
    'TRANSACTION_DATE',
    'STAKEHOLDER',
    'SHARE_CLASS',
    'NO_OF_SHARES',
    'SIGNATURE_STATUS',
    'FILE',
    'EMPTY',
  ],
  [EventTypeBackend.FUNDRAISING_ROUND]: [
    'TRANSACTION_DATE',
    'STAKEHOLDER',
    'VALUATION_EVENT',
    'INVESTMENT',
    'NO_OF_SHARES',
    'SHARE_CLASS',
    'PRE_MONEY_VALUATION',
    'POST_MONEY_VALUATION',
    'SIGNATURE_STATUS',
    'FILE',
    'EMPTY',
  ],
  [EventTypeBackend.NEW_PLAN]: [
    'TRANSACTION_DATE',
    'NAME',
    'EQUITY_POOL',
    'EQUITY_PLAN_TYPE',
    'SIGNATURE_STATUS',
    'FILE',
    'EMPTY',
  ],
  [EventTypeBackend.NEW_POOL]: [
    'TRANSACTION_DATE',
    'TRANSACTION_TYPE',
    'NAME',
    'NO_OF_FULLY_DILUTED_SHARES',
    'SHARE_CLASS',
    'SIGNATURE_STATUS',
    'FILE',
    'EMPTY',
  ],
  [EventTypeBackend.POOL_INCREASE]: poolAdjustmentTableHead,
  [EventTypeBackend.POOL_DECREASE]: poolAdjustmentTableHead,
  [EventTypeBackend.SHARE_ISSUANCE]: [
    'TRANSACTION_DATE',
    'STAKEHOLDER',
    'VALUATION_EVENT',
    'SHARE_CLASS',
    'SHARES_PRICE',
    'NO_OF_SHARES',
    'PRE_MONEY_VALUATION',
    'POST_MONEY_VALUATION',
    'SIGNATURE_STATUS',
    'FILE',
    'EMPTY',
  ],
  [EventTypeBackend.SECONDARIES]: [
    'TRANSACTION_DATE',
    'FROM_STAKEHOLDER',
    'TO_STAKEHOLDER',
    'SHARE_CLASS',
    'NO_OF_SHARES',
    'SHARE_VALUE',
    'TRANSFER_AMOUNT',
    'SIGNATURE_STATUS',
    'FILE',
    'EMPTY',
  ],
  [EventTypeBackend.VALUATION]: [
    'TRANSACTION_DATE',
    'VALUATION_EVENT_NAME',
    'SHARES_PRICE',
    'PRE_MONEY_VALUATION',
    'SIGNATURE_STATUS',
    'FILE',
  ],

  [EventTypeBackend.SAFE_CONVERSION]: [
    'TRANSACTION_DATE',
    'STAKEHOLDER',
    'INVESTMENT',
    'CONVERSION_METHOD',
    'LINKED_EVENT',
    'SHARE_PRICE',
    'NO_OF_SHARES',
    'SIGNATURE_STATUS',
    'FILE',
    'EMPTY',
  ],
  [EventTypeBackend.SAFE]: [
    'ISSUE_DATE',
    'STAKEHOLDER',
    'INVESTMENT',
    'CAP_VALUE',
    'CAP_TYPE',
    'FLOOR',
    'DISCOUNT',
    'MATURITY_DATE',
    'SIGNATURE_STATUS',
    'FILE',
  ],
  [EventTypeBackend.COMPANY_INCORPORATION]: [
    'TRANSACTION_DATE',
    'STAKEHOLDER',
    'SHARE_CLASS',
    'NO_OF_SHARES',
    'PAR_VALUE',
    'CONSIDERED_PAY',
    'FILE',
    'EMPTY',
  ],
};

export const getTransactionLoadingRowCount = (event: EventUnion, itemsToFetch: number): number => {
  let hint = 1;

  if ('exercise' in event && event.exercise?.items?.length) {
    hint = event.exercise.items.length;
  } else if ('items' in event && Array.isArray(event.items) && event.items.length > 0) {
    hint = event.items.length;
  }

  return Math.max(1, Math.min(hint, itemsToFetch));
};

export const getTransactionsTableStateProps = ({
  selectedDateFilter,
  selectedCustomDateFilter,
  transactionView,
  isLoading,
  itemsCount,
  event,
  itemsToFetch,
}: {
  event: EventUnion;
  isLoading: boolean;
  itemsCount: number;
  itemsToFetch: number;
  selectedCustomDateFilter: DateRange;
  selectedDateFilter: SortByDate;
  transactionView?: boolean;
}) => ({
  isEmpty: !isLoading && itemsCount === 0,
  isFilterResult:
    !!transactionView ||
    selectedDateFilter !== SortByDate.ALL_TIME ||
    selectedCustomDateFilter?.from !== undefined ||
    selectedCustomDateFilter?.to !== undefined,
  isLoading,
  loadingRows: getTransactionLoadingRowCount(event, itemsToFetch),
});
